/**
 * /earn/submit — Submit an Action.
 *
 * The single "front door" for every stub CTA in the SocialCapital nav
 * tree. Signed-in users log a contribution here; it lands in
 * ``action_reports`` with status="pending" and appears in their
 * "My submissions" list below the form.
 *
 * No point award happens on submit — moderators (or a future auto-
 * verify pass) decide if the action becomes a scored ``point_events``
 * row on the user's passport.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2, Clock, XCircle, Info, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import { toast } from "sonner";

const STATUS_META = {
  pending:  { label: "Pending review", color: "#B45309", bg: "#FEF3C7", Icon: Clock },
  approved: { label: "Approved",        color: "#065F46", bg: "#D1FAE5", Icon: CheckCircle2 },
  rejected: { label: "Not counted",     color: "#991B1B", bg: "#FEE2E2", Icon: XCircle },
};

export default function SocialCapitalSubmitPage() {
  const { user } = useAuth() || {};
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [occurredOn, setOccurredOn] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [location, setLocation] = useState("");
  const [hours, setHours] = useState("");
  const [people, setPeople] = useState("");
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({});
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    http.get("/social-capital/categories")
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => {});
  }, []);

  const loadMine = useCallback(async () => {
    if (!user) return;
    setLoadingList(true);
    try {
      const { data } = await http.get("/social-capital/submissions/mine");
      setSubmissions(data?.submissions || []);
      setCounts(data?.counts || {});
    } catch {
      // silent — anonymous users won't hit this branch
    } finally {
      setLoadingList(false);
    }
  }, [user]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Please pick a category."); return; }
    if (title.trim().length < 3) { toast.error("Give your action a short title."); return; }
    if (description.trim().length < 10) { toast.error("A bit more description helps a reviewer."); return; }
    setSaving(true);
    try {
      const payload = {
        category,
        title: title.trim(),
        description: description.trim(),
        occurred_on: occurredOn || null,
        evidence_url: evidenceUrl.trim() || null,
        location: location.trim() || null,
        hours: hours ? Number(hours) : null,
        people_impacted: people ? Number(people) : null,
      };
      await http.post("/social-capital/submissions", payload);
      toast.success("Submitted — a reviewer will look at it shortly.");
      setTitle(""); setDescription(""); setOccurredOn(""); setEvidenceUrl("");
      setLocation(""); setHours(""); setPeople("");
      loadMine();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save your submission.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  }, [category, title, description, occurredOn, evidenceUrl, location, hours, people, loadMine]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="sc-submit-signed-out">
        <SeoHead section="social-capital" title="Submit an Action · SocialCapital.cc"
                 description="Log a contribution you've already made. Sign in first — it takes 30 seconds."
                 url="/earn/submit" />
        <div className="rounded-3xl p-10 text-center border"
             style={{ background: "#ECFDF5", borderColor: "rgba(5,150,105,0.25)" }}>
          <Sparkles className="w-12 h-12 mx-auto mb-4" style={{ color: "#059669" }} />
          <h1 className="font-display text-3xl sm:text-4xl mb-3">Log an action you took</h1>
          <p className="opacity-80 mb-6 max-w-lg mx-auto">
            You need a free account to submit contributions. It takes about 30 seconds and everything
            you log becomes part of your lifetime Social Capital passport.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold text-white"
                  style={{ background: "#059669" }}
                  data-testid="sc-submit-signup">
              Create free account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold border"
                  style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  data-testid="sc-submit-signin">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 md:py-14" data-testid="sc-submit-page">
      <SeoHead section="social-capital" title="Submit an Action · SocialCapital.cc"
               description="Log a contribution you've already made — we'll help you get it verified and counted."
               url="/earn/submit" />

      <header className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "#059669" }}>
          Earn Social Capital
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold" style={{ color: "#0f172a" }}>
          Submit an Action
        </h1>
        <p className="mt-3 text-base opacity-80 max-w-2xl">
          Log a contribution you've already made. A reviewer will check it and — once verified —
          it becomes part of your permanent Social Capital passport.
        </p>
      </header>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Form */}
        <form onSubmit={submit} className="md:col-span-2 space-y-4" data-testid="sc-submit-form">
          <Field label="Category" required>
            <select
              value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 bg-white"
              style={{ borderColor: "rgba(0,0,0,0.15)" }}
              data-testid="sc-submit-category"
            >
              <option value="">Choose a category…</option>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Short title" required hint="e.g. Distributed 30 meals at the community kitchen">
            <input
              value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
              className="w-full rounded-lg border px-3 py-2 bg-white"
              style={{ borderColor: "rgba(0,0,0,0.15)" }}
              data-testid="sc-submit-title"
            />
          </Field>

          <Field label="Describe what you did" required hint="Reviewers look for clear, honest detail — impact matters more than length.">
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={6}
              className="w-full rounded-lg border px-3 py-2 bg-white leading-relaxed"
              style={{ borderColor: "rgba(0,0,0,0.15)" }}
              data-testid="sc-submit-description"
            />
            <div className="text-xs opacity-60 text-right mt-1">{description.length}/2000</div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Date it happened" hint="Optional">
              <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)}
                     className="w-full rounded-lg border px-3 py-2 bg-white"
                     style={{ borderColor: "rgba(0,0,0,0.15)" }}
                     data-testid="sc-submit-occurred" />
            </Field>
            <Field label="Location" hint="Optional — city or organization">
              <input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200}
                     className="w-full rounded-lg border px-3 py-2 bg-white"
                     style={{ borderColor: "rgba(0,0,0,0.15)" }}
                     data-testid="sc-submit-location" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Hours you spent" hint="Optional">
              <input type="number" min="0" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)}
                     className="w-full rounded-lg border px-3 py-2 bg-white"
                     style={{ borderColor: "rgba(0,0,0,0.15)" }}
                     data-testid="sc-submit-hours" />
            </Field>
            <Field label="People impacted" hint="Optional — a rough estimate is fine">
              <input type="number" min="0" step="1" value={people} onChange={(e) => setPeople(e.target.value)}
                     className="w-full rounded-lg border px-3 py-2 bg-white"
                     style={{ borderColor: "rgba(0,0,0,0.15)" }}
                     data-testid="sc-submit-people" />
            </Field>
          </div>

          <Field label="Evidence link" hint="Optional — photo, article, event page, org confirmation, etc.">
            <input type="url" value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)}
                   placeholder="https://…" maxLength={800}
                   className="w-full rounded-lg border px-3 py-2 bg-white"
                   style={{ borderColor: "rgba(0,0,0,0.15)" }}
                   data-testid="sc-submit-evidence" />
          </Field>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-semibold text-white disabled:opacity-60"
                    style={{ background: "#059669" }}
                    data-testid="sc-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {saving ? "Submitting…" : "Submit for review"}
            </button>
            <span className="text-xs opacity-70 inline-flex items-center gap-1">
              <Info className="w-3.5 h-3.5" /> A reviewer typically responds within 3 days.
            </span>
          </div>
        </form>

        {/* Side rail — my submissions */}
        <aside className="space-y-4" data-testid="sc-submit-side">
          <div className="rounded-2xl border p-5" style={{ borderColor: "rgba(0,0,0,0.08)", background: "#F9FAFB" }}>
            <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "#059669" }}>
              Your submissions
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {["pending", "approved", "rejected"].map((k) => (
                <span key={k}
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{ background: STATUS_META[k].bg, color: STATUS_META[k].color }}
                      data-testid={`sc-submit-count-${k}`}>
                  <span className="tabular-nums">{counts[k] || 0}</span>
                  <span>{STATUS_META[k].label}</span>
                </span>
              ))}
            </div>
            {loadingList ? (
              <div className="text-sm opacity-60">Loading…</div>
            ) : submissions.length === 0 ? (
              <div className="text-sm opacity-60">
                Nothing submitted yet. Your entries will appear here.
              </div>
            ) : (
              <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {submissions.map((s) => {
                  const meta = STATUS_META[s.status] || STATUS_META.pending;
                  const Icon = meta.Icon;
                  return (
                    <li key={s.id} className="rounded-xl border p-3 bg-white"
                        style={{ borderColor: "rgba(0,0,0,0.06)" }}
                        data-testid={`sc-submit-row-${s.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                        <span className="text-[11px] font-mono uppercase tracking-wider"
                              style={{ color: meta.color }}>{meta.label}</span>
                        <span className="ml-auto text-[11px] opacity-60">
                          {(s.created_at || "").slice(0, 10)}
                        </span>
                      </div>
                      <div className="font-semibold text-sm mb-0.5">{s.title}</div>
                      <div className="text-xs opacity-70 line-clamp-2">{s.description}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold mb-1">
        {label} {required && <span style={{ color: "#DC2626" }}>*</span>}
      </div>
      {children}
      {hint && <div className="text-xs opacity-60 mt-1">{hint}</div>}
    </label>
  );
}
